/**
 * @format
 * @module lib/audience/bootstrap
 */
import tgamConfig from "config/tgam";
import {
  roles,
  isLoggedIn,
  isAnonymous,
  isSubscribed,
  isRegistered,
  isOnDemand,
  getUserRoleFromServiceString,
  getUserRegionByPostal,
  getAnalyticsSession,
  setAfterLoginCookie,
  deleteUserCookies
} from "lib/identity/api";
import { mark, measure } from "lib/performance/user-timing";
import { local } from "lib/store";
import exposeDarwinMethods from "lib/darwin";
import {
  declareDarwinPlugin,
  runDarwinTestSegments,
  runDarwinTestsBefore,
  runDarwinTestsAfter
} from "lib/darwin/tests";
import ee from "lib/eventemitter";
import {
  datalayerSetup,
  datalayerUpdate,
  datalayerReadyResolve,
  datalayerLoggerInfo
} from "./datalayer";
import dataCollection from "lib/audience/data-collection";
import crow from "utils/crow";
import { API_HOST } from "lib/gamservices";
import playKeytar from "lib/commercial/keytar";
import { initRegwallModals } from "ui/user-states";
import initNativeAppCTAModal from "ui/native-app-cta-modal";
import initMobileModal from "ui/user-mobile-modal";
import Hermes from "lib/commercial/keytar/Hermes";
import { getProp } from "utils/utils";
import logger from "utils/logger";
import isNil from "lodash/isNil";
import keytarDone from "lib/commercial/keytar/keytar-done";
import { hasParameterMatch } from "lib/commercial/keytar/helpers";
import { enterpriseAccess } from "./enterprise-access";
import { debugChurn } from "lib/audience/propensity";
import {
  addEntitlement,
  removeEntitlement,
  ENTITLEMENTS,
  DEMANDS
} from "lib/identity/entitlements";
import datalayerReady from "lib/audience/datalayer/ready";

const debugTag = "[AUDIENCE-BOOTSTRAP]";

// this object is used to determine if keytar should be
// bypassed when certain network errors occur when calling
// the "User management api" (e.g. error code 5xx or 406)
let bypassKeytar = {
  shouldBypass: false,
  status: null
};

/**
 * @method addLastAccessBeacon
 * @description Adds a last access beacon gif to the page when the correct user state is detected.
 */
export function installLastAccessBeacon() {
  if (isAnonymous()) {
    return;
  }
  const i = document.createElement("img");
  i.src = `${API_HOST}/api/v3/user/last-access-gif`;
  i.style.display = "none";
  i.width = 1;
  i.height = 1;
  document.body.appendChild(i);
}

/**
 * Initialize Keytar
 * @param {Object} [hermesData={}] - data from the Hermes API call
 */
function initKeytar(hermesData = {}) {
  const id = getProp(tgamConfig, "datalayer.identity", {});
  const paywallStatus = getProp(tgamConfig, "datalayer.article.paywallStatus", "");
  const contentId = getProp(tgamConfig, "meta.contentId", "");

  const searchParams = new URLSearchParams(document.location.search);
  /**
   * Support appending count to contentId value, for Nightwatch tests.
   * This allows using the same article, while also incrementing the wall meter count.
   * @type {String}
   */
  const nwc = searchParams.get("nwc") || "";

  playKeytar(id, bypassKeytar, hermesData, paywallStatus, contentId + nwc);
}

/**
 * @method onDataLayerReady
 * @description Invoke an array of methods after the datalayer has been setup.
 */
function onDataLayerReady() {
  mark("onDataLayerReady_started");
  exposeDarwinMethods(); // used within Darwin variant test methods.
  runDarwinTestSegments(); // determines which (before/after) variants will run.
  declareDarwinPlugin(); // used by addDarwinTestsToDatalayer()

  keytarDone.then(data => {
    initNativeAppCTAModal();
    initMobileModal();

    if (data.accessAllowed) {
      runDarwinTestsAfter(); // after keytar
    }
    dataCollection(); // initializes the event handler for analytics.trackGTMEvent
    if (data.shouldBypass) {
      ee.trigger("analytics.trackGTMEvent", [
        {
          type: "api",
          component: "usermanagement",
          description: `User Management call returned error ${data.status}`,
          affected_event: "wall_hit"
        },
        "errorEvent"
      ]);
    }
    window.analyticsLayer.push({ event: "Arc Ready" });
  });

  initRegwallModals();

  mark("keytar_started");

  const H = new Hermes();

  // Currently, we're allowing newsletter visitors with a matching story query string parameter value (defined in runtime properties)
  // to view articles for free, only if logged in, regardless of its paywall status (i.e., yellow, red, etc.). If this proves
  // successful, a better longer-term strategy will replace this implementation.
  const freeToView = hasParameterMatch();

  if (!freeToView && H.enabled && (isAnonymous() || isRegistered()) && !isOnDemand()) {
    logger.info(`${debugTag} Hermes API called`);

    H.fetch().then(data => {
      // When the API call fails, data is undefined
      const hermes = !isNil(data) ? data : {};

      // Decide whether to pass the Hermes API response data to Keytar so that
      // it can determine whether to show a paywall or meter, based on that input
      const passHermesData = H.passDataToKeytar;
      const hermesData = passHermesData ? { ...hermes, algorithmName: H.algorithm } : {};

      logger.info(`${debugTag} hermesPassDataToKeytar: ${H.passDataToKeytar}`);
      logger.info(`${debugTag} hermesHideSubscriberKeys: ${H.hideSubscriberKeys}`);

      // Decide whether to hide the "for subscribers" keys
      if (H.hideSubscriberKeys) {
        hideSubscriberKeys();
      }
      // Can't use datalayer.add() here because datalayer.setup() has already run
      H.dispatchEvent();
      window.tgam.datalayer.hermesData = hermesData;
      window.tgam.datalayer.hermesPassDataToKeytar = passHermesData;

      // run Darwin tests before Keytar, passing the Hermes response (or nothing, if the API failed
      // or hermesPassDataToKeytar is false) as an argument to each test
      if (Object.keys(hermesData).length > 0) {
        runDarwinTestsBefore(hermesData);
      } else {
        runDarwinTestsBefore();
      }
      initKeytar(hermesData);
    });
  } else {
    logger.info(`${debugTag} Hermes API not called`);

    if (!freeToView) {
      logger.info(
        `${debugTag} Darwin takeover wall tests are allowed to run since no free to view access was detected`
      );
      runDarwinTestsBefore();
    }

    initKeytar();
  }

  mark("keytar_done");
  measure("keytar");

  mark("onDataLayerReady_done");
  measure("onDataLayerReady");
}

/**
 * Hide all the "Subscriber content" key icons and other indicators
 */
function hideSubscriberKeys() {
  logger.info(`${debugTag} Hiding keys`);
  document.documentElement.classList.add("u-hide-keys");

  // TODO: Delete the following after ARC-5164 has been UAT-approved
  if (/debugHideKeys=true/.test(window.location.search)) {
    document.documentElement.classList.add("debug-hide-keys");
  }
}

/**
 * @method handleUnload
 * @description Set the current page into local storage, so analytics can process it on the next page view.
 */
function handleUnload() {
  const lastPage = tgamConfig.meta.urlRelative;
  if (lastPage) {
    local.set("tgam.analytics.secondLastPageViewed", lastPage);
  }
}

/**
 * Adds additional information to the user data object, sets user
 * entitlements, etc., and returns the user data object.
 * @param {Object} [userData={}] - of the analytics profile
 * @returns {Object}
 */
function resolveUserApiResponse(userData = {}) {
  // If and when the business decides to resurrect the IP ring functionality, delete the following
  // code line because there will be no reason to remove the "on demand" value from the service
  // string. This entitlement value is also used for users with enterprise access.
  userData.ssvcs = removeEntitlement(userData.ssvcs, ENTITLEMENTS.ONDEMAND.value);

  const isUserLoggedIn = isLoggedIn();
  userData.role = !isUserLoggedIn ? roles.anonymous : getUserRoleFromServiceString(userData.ssvcs);

  if (userData.role.id === roles.anonymous.id || userData.role.id === roles.registered.id) {
    // We set this cookie for all but subscribed users because the assumption is registered users
    // _may_ go through the subscription process and we want them to end up on the page that they
    // clicked the "subscribe now" button on after they have completed their GU registration.
    setAfterLoginCookie();
  }
  if (isUserLoggedIn) {
    userData.region = getUserRegionByPostal(userData);
    userData.propensity = debugChurn(userData.propensity);
  }
  return userData;
}

/**
 * Log the session error and continue bootstrapping the datalayer for an anonymous user.
 * @param {Error} error - of failed http request
 * @returns {Object}
 */
function rejectUserApiResponse(error) {
  if (shouldBypassKeytar(error)) {
    bypassKeytar = {
      shouldBypass: true,
      status: error.status
    };
  }

  const title = "Bootstrap : rejectUserApiResponse";
  const message = "Failed to retrieve user identity";
  console.error(title, message);
  console.error(error);

  // for Keytar purposes, override the system prop set in environment-variables.jsp
  // also note the string "false" here: done this way for consistency since the
  // system prop also comes back with a string value
  tgamConfig.keytar.enabled = "false";
  tgamConfig.keytar.stateMessage = message;
  crow.error(new Error(message), {
    feature: title
  });

  // initialize the window.tgam.datalayer.identity.role to
  // an anonymous user when the "user management api" fails
  return { role: roles.anonymous };
}

/**
 * Determine if keytar should run based on response status of user management api call
 * @param {Object} res - The response object from user management API call
 * @returns {Boolean} - true if keytar should not run
 */
function shouldBypassKeytar(res) {
  const is5xx = res.status >= 500 && res.status <= 511;
  return is5xx || res.status === 406;
}

/**
 * @method bootstrap
 * @description bootstrap identifies current user and loads analytics dependencies
 */
export default function bootstrap() {
  mark("audience_bootstrap_started");
  datalayerReady.then(onDataLayerReady);
  window.addEventListener("unload", handleUnload, false);

  const isUserLoggedIn = isLoggedIn();
  preventCrossEnvSessions(isUserLoggedIn);

  processUser(isUserLoggedIn)
    .then(userData => {
      datalayerSetup();
      datalayerUpdate({ identity: userData });
      return processEnterpriseAccess(userData, isUserLoggedIn);
    })
    .then(userData => {
      // User might have on-demand access now, so update identity in datalayer.
      datalayerUpdate({ identity: userData });
      datalayerLoggerInfo();
      datalayerReadyResolve();

      mark("audience_bootstrap_done");
      measure("audience_bootstrap");
    });
}

/**
 * Process the current user to identify if they are authenticated or not.
 * @param {Boolean} isUserLoggedIn - if the user has a session
 * @returns {Promise}
 */
function processUser(isUserLoggedIn) {
  // If and when the business decides to resurrect the IP ring functionality, we'd need to start
  // calling the analytics API 100% of the time. Regardless if user is logged in or not.
  return (isUserLoggedIn ? getAnalyticsSession() : Promise.resolve())
    .then(resolveUserApiResponse)
    .catch(rejectUserApiResponse);
}

/**
 * This is a hack. If you're logged into prod or staging, you'll still be logged in there.
 * It helps prevent cross environment logins, which _usually_ won't work.
 * @param {Boolean} isUserLoggedIn - if the user has a session
 */
function preventCrossEnvSessions(isUserLoggedIn) {
  if (isUserLoggedIn) {
    return;
  }
  const currentEnv = getProp(tgamConfig, "env.name", "production").toLowerCase();
  if (currentEnv !== "development") {
    deleteUserCookies();
  }
}

/**
 * Check if we should grant the (partner) user enterprise access (on-demand).
 * @param {Object} [userData={}] - of user data
 * @param {Boolean} isUserLoggedIn - if the user has a session
 * @returns {Promise<Object>} of updated user data
 */
function processEnterpriseAccess(userData = {}, isUserLoggedIn) {
  // If user is a subscriber everything remains the same, no enterprise access necessary.
  if (isUserLoggedIn && isSubscribed()) {
    return Promise.resolve(userData);
  }

  // If user has no partner Id, then deny enterprise access.
  return enterpriseAccess().then(partnerId => {
    if (!partnerId) {
      return userData;
    }

    // Authentication successful, grant the user enterprise access on-demand entitlement.
    userData.ssvcs = addEntitlement(userData.ssvcs, ENTITLEMENTS.ONDEMAND.value);
    userData.demand = DEMANDS.ENTERPRISE; // on-demand subtype
    return userData;
  });
}
